import { toast } from 'App';
import clsx from 'clsx';
import { Button } from 'components/button';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import TextArea from 'components/fields/TextArea';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ScrollToBottom from 'react-scroll-to-bottom';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';
import { errorToText } from 'utils/error-to-text';
import { supportService } from 'views/services/support';
import { SenderType } from 'views/types/support';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { ReadPermissionGate } from 'components/PermissionGate';

dayjs.extend(relativeTime);

export const SupportChats = () => {
  const selectedChatId = useParams().chatId;
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounceValue(search, 500);
  const [chatSort, setChatSort] = useState<'all' | 'recent' | 'oldest'>('all');
  const { hasPermission, canRead, canCreate, canUpdate } = usePermissions();

  const navigation = useNavigate();

  const {
    data: chats,
    isLoading: isChatsLoading,
    mutate: mutateChat,
  } = useSWR(
    ['/api/chats', debouncedSearch, chatSort],
    () => supportService.getChats(debouncedSearch, chatSort),
    {
      revalidateOnFocus: false,
      refreshInterval: 5000,
    }
  );

  const {
    data: messages,
    isLoading: isMessagesLoading,
    mutate,
  } = useSWR(
    selectedChatId ? `/api/messages/${selectedChatId}` : null,
    () => supportService.getMessages(selectedChatId),
    {
      revalidateOnFocus: true,
      refreshInterval: 3000,
    }
  );

  const form = useForm({
    defaultValues: {
      message: '',
    },
  });

  const handleSubmit = async (data: { message: string }) => {
    if (!selectedChatId) return;

    if (!canRead(PERMISSION_RESOURCES.SEND_CTA)) {
      toast({
        title: 'Access Denied',
        description:
          'You do not have permission to send messages in support chat',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await supportService.sendMessage(selectedChatId, data.message);
      await mutateChat();
      await mutate();
      form.reset();
    } catch (error) {
      toast({
        title: 'Error',
        description: errorToText(error),
        status: 'error',
      });
    }
  };

  const onLeaveChat = async () => {
    if (!hasPermission(PERMISSION_RESOURCES.LEAVE_CHAT_CTA, 'update')) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to leave this chat',
        status: 'error',
      });
      return;
    }

    try {
      navigation('/admin/support-chats/');
      await supportService.leaveChat(selectedChatId);
    } catch (error) {
      toast({
        title: 'Error',
        description: errorToText(error),
        status: 'error',
      });
    }
  };

  return (
    <div>
      <Card extra="w-full h-full md:h-[80vh] md:min-h-[500px] mt-9 px-6">
        <div className="grid grid-cols-[240px,1fr] h-full gap-6">
          <div className="flex flex-col pt-6 max-h-full grow overflow-hidden">
            {canRead(PERMISSION_RESOURCES.SEARCH_BUTTON_SUPPORT) && (
              <InputField
                id={''}
                extra={'-mt-2'}
                placeholder={'Search...'}
                variant={'auth'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                disabled={
                  !canUpdate(PERMISSION_RESOURCES.SUPPORT_CHATS) ||
                  !canUpdate(PERMISSION_RESOURCES.SEARCH_BUTTON_SUPPORT)
                }
              />
            )}
            {canRead(PERMISSION_RESOURCES.FILTER_BUTTON_SUPPORT) && (
              <select
                className="w-full mt-3 mb-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 placeholder:text-gray-400 outline-none dark:bg-navy-800 dark:border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                value={chatSort}
                onChange={e =>
                  setChatSort(e.target.value as 'all' | 'recent' | 'oldest')
                }
                disabled={
                  !canUpdate(PERMISSION_RESOURCES.SUPPORT_CHATS) ||
                  !canUpdate(PERMISSION_RESOURCES.FILTER_BUTTON_SUPPORT)
                }
              >
                <option value="all">All Chats</option>
                <option value="recent">Most Recent</option>
                <option value="oldest">Oldest First</option>
              </select>
            )}
            <div className="relative flex flex-col gap-2 overflow-auto grow pb-6 pt-2">
              {isChatsLoading && (
                <div className="flex justify-center items-center h-full text-gray-500">
                  <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                </div>
              )}
              {!isChatsLoading && !chats?.data.length && (
                <div className="flex justify-center items-center h-full text-gray-500">
                  No chats found
                </div>
              )}
              <div className="overflow-y-auto flex-1">
                {chats?.data.map(chat => (
                  <Link
                    to={`/admin/support-chats/${chat.id}`}
                    key={chat.id}
                    className="grid grid-cols-1 h-max mb-2"
                  >
                    <Button
                      variant={
                        selectedChatId === chat.id ? 'primary' : 'secondary'
                      }
                      key={chat.id}
                      title={
                        chat.title === 'Telegram Chat'
                          ? `Chat with ${chat.user.username}`
                          : chat.title
                      }
                    >
                      <span className="truncate">
                        {chat.title === 'Telegram Chat'
                          ? `Chat with ${chat.user.username}`
                          : chat.title}
                      </span>
                    </Button>
                  </Link>
                ))}
              </div>
              {!!selectedChatId && (
                <ReadPermissionGate
                  resource={PERMISSION_RESOURCES.LEAVE_CHAT_CTA}
                >
                  <Button
                    disabled={
                      !canUpdate(PERMISSION_RESOURCES.SUPPORT_CHATS) ||
                      !canUpdate(PERMISSION_RESOURCES.LEAVE_CHAT_CTA)
                    }
                    onClick={onLeaveChat}
                    className="mt-auto"
                  >
                    Leave the Chat
                  </Button>
                </ReadPermissionGate>
              )}
            </div>
          </div>

          <div className="flex flex-col h-full overflow-hidden pb-6">
            <ScrollToBottom
              className="flex flex-col overflow-hidden grow"
              scrollViewClassName="flex flex-col gap-2 overflow-auto grow pt-6 pb-2"
              followButtonClassName="hidden"
            >
              {!selectedChatId && !isMessagesLoading && (
                <div className="flex justify-center items-center h-full text-gray-500">
                  Select chat to view messages
                </div>
              )}
              {isMessagesLoading && selectedChatId && (
                <div className="flex justify-center items-center h-full text-gray-500">
                  <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                </div>
              )}
              {!isMessagesLoading &&
                selectedChatId &&
                !messages?.data.length && (
                  <div className="flex justify-center items-center h-full text-gray-500">
                    No messages found
                  </div>
                )}
              {!isMessagesLoading &&
                selectedChatId &&
                messages?.data.map(message => (
                  <div
                    key={message.id}
                    className={clsx(
                      'flex flex-col p-1.5 rounded-lg text-sm',
                      message.senderType === SenderType.USER &&
                        'bg-gray-100 dark:bg-gray-800 mr-auto max-w-[80%]',
                      message.senderType === SenderType.AI &&
                        'bg-blue-100 dark:bg-blue-800 ml-auto max-w-[80%]'
                    )}
                  >
                    <pre className="font-[inherit] whitespace-break-spaces">
                      {message.content}
                    </pre>
                    <div
                      title={dayjs(message.createdAt).toISOString()}
                      className={clsx(
                        'text-[0.625rem] opacity-50',
                        message.senderType === SenderType.USER && 'ml-auto',
                        message.senderType === SenderType.AI && 'mr-auto'
                      )}
                    >
                      {dayjs(message.createdAt).fromNow()}
                    </div>
                  </div>
                ))}
            </ScrollToBottom>
            {!!selectedChatId && (
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="flex gap-2 items-end"
              >
                <ReadPermissionGate resource={PERMISSION_RESOURCES.SEND_CTA}>
                  <TextArea
                    id={''}
                    extra="grow -mt-2"
                    extraTextarea="mt-0 grow min-h-12 max-h-40"
                    placeholder={''}
                    variant={''}
                    {...form.register('message', {
                      required: 'This field is required',
                    })}
                    state={
                      form.formState.errors.message?.message
                        ? 'error'
                        : undefined
                    }
                    disabled={
                      isChatsLoading ||
                      isMessagesLoading ||
                      form.formState.isSubmitting ||
                      !canUpdate(PERMISSION_RESOURCES.SUPPORT_CHATS) ||
                      !canCreate(PERMISSION_RESOURCES.SEND_CTA) ||
                      !canUpdate(PERMISSION_RESOURCES.SEND_CTA)
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (canRead(PERMISSION_RESOURCES.SEND_CTA)) {
                          form.handleSubmit(handleSubmit)();
                        }
                      }
                    }}
                  />
                </ReadPermissionGate>

                <ReadPermissionGate resource={PERMISSION_RESOURCES.SEND_CTA}>
                  <Button
                    type="submit"
                    disabled={
                      isChatsLoading ||
                      isMessagesLoading ||
                      form.formState.isSubmitting ||
                      !form.formState.isDirty ||
                      !canUpdate(PERMISSION_RESOURCES.SUPPORT_CHATS) ||
                      !canCreate(PERMISSION_RESOURCES.SEND_CTA) ||
                      !canUpdate(PERMISSION_RESOURCES.SEND_CTA)
                    }
                  >
                    Send
                  </Button>
                </ReadPermissionGate>
              </form>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
